import React from 'react';
import { ArrowRight, Package, Truck, Plane, Shield, CheckCircle, Phone, Mail, MapPin, Clock, Globe } from 'lucide-react';
import ResponsiveNavbar from './ResponsiveNavbar';

const HomePage: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <ResponsiveNavbar />

      {/* Hero Section with Background and Animations */}
      <section id="home" className="relative min-h-screen pt-20 flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80"
            alt="Logistics Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-800/70 to-gray-900/80"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/30"></div>
        </div>

        {/* Animated Background Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-red-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-16 w-24 h-24 bg-green-500/15 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-blue-500/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-20 right-1/3 w-16 h-16 bg-red-500/20 rounded-full blur-xl animate-pulse" style={{ animationDelay: '0.5s' }}></div>

        {/* Main Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Trust Badge */}

          {/* Main Heading */}
          <div className="mb-8">
            <h1 className="text-3xl sm:text-6xl lg:text-7xl font-bold leading-tight">
              <span className="block text-white opacity-0 animate-slideInUp" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
                Nigeria's Leading
              </span>
              <span className="block bg-gradient-to-r from-red-400 via-red-500 to-red-600 bg-clip-text text-transparent opacity-0 animate-slideInUp" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}>
                Logistics Partner
              </span>
              <span className="block text-white opacity-0 animate-slideInUp" style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}>
                Connected to the World
              </span>
            </h1>
          </div>

          {/* Description */}
          <p className="text-md sm:text-2xl text-gray-200 mb-12 max-w-4xl mx-auto leading-relaxed opacity-0 animate-slideInUp" style={{ animationDelay: '1s', animationFillMode: 'forwards' }}>
            As a proud member of Skynet Worldwide Express, CFL Global Logistics delivers your packages, cargo,
            and documents anywhere in Nigeria and across the globe with unmatched reliability and speed.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16 opacity-0 animate-slideInUp" style={{ animationDelay: '1.2s', animationFillMode: 'forwards' }}>
            <a href='/quote' className="group relative bg-gradient-to-r from-red-600 to-red-700 text-white px-10 py-5 rounded-2xl font-bold text-lg hover:from-red-700 hover:to-red-800 transition-all duration-500 shadow-2xl hover:shadow-red-500/25 transform hover:-translate-y-2 hover:scale-105 flex items-center justify-center overflow-hidden">
              <span className="relative z-10 flex items-center">
                Get Instant Quote
                <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-red-700 to-red-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </a>
            <a href='/tracking' className="group border-3 border-white/60 text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-white hover:text-gray-900 transition-all duration-500 backdrop-blur-sm flex items-center justify-center transform hover:-translate-y-1 hover:scale-105 shadow-xl">
              <Package className="w-6 h-6 mr-3 group-hover:rotate-12 transition-transform duration-300" />
              Track Your Package
            </a>
          </div>

          {/* Animated Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 opacity-0 animate-slideInUp" style={{ animationDelay: '1.4s', animationFillMode: 'forwards' }}>
            {[
              { number: '5+', label: 'Years of Excellence', delay: '1.6s' },
              { number: '500+', label: 'Daily Deliveries', delay: '1.8s' },
              { number: '36', label: 'States Covered', delay: '2s' },
              { number: '99.8%', label: 'On-Time Delivery', delay: '2.2s' }
            ].map((stat, index) => (
              <div
                key={index}
                className={`bg-white/15 backdrop-blur-md rounded-2xl p-8 shadow-2xl transform hover:scale-110 transition-all duration-300 border border-white/20 opacity-0 animate-slideInUp`}
                style={{ animationDelay: stat.delay, animationFillMode: 'forwards' }}
              >
                <div className="text-4xl lg:text-5xl font-bold text-white mb-3 animate-countUp">
                  {stat.number}
                </div>
                <div className="text-gray-200 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 animate-bounce" style={{ animationDelay: '3s', animationFillMode: 'forwards' }}>
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Why Choose CFL */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">Why Leading Nigerian Businesses Trust CFL Global</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine global reach with local expertise to deliver exceptional logistics solutions
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Globe className="w-8 h-8" />,
                title: 'Skynet Network',
                description: 'Part of the world\'s largest independent express delivery network with global reach and local expertise.'
              },
              {
                icon: <Clock className="w-8 h-8" />,
                title: 'Real-Time Tracking',
                description: 'Know exactly where your shipment is, every step of the way with our advanced tracking technology.'
              },
              {
                icon: <Shield className="w-8 h-8" />,
                title: 'GIT Insurance',
                description: 'Your cargo is protected with comprehensive Goods in Transit insurance coverage for peace of mind.'
              }
            ].map((value, index) => (
              <div key={index} className="group p-8 rounded-3xl bg-gradient-to-br from-gray-50 to-red-50 hover:from-red-50 hover:to-green-50 transition-all duration-500 hover:shadow-2xl hover:scale-105">
                <div className="w-18 h-18 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-50 to-green-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">Complete Logistics Solutions for Every Need</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From urgent documents to heavy cargo, we move it all with precision and care
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Package className="w-12 h-12" />,
                title: 'Express Document Delivery',
                description: 'Same-day and next-day delivery with real-time tracking and secure handling.',
                features: ['Same-day delivery', 'Real-time tracking', 'Secure handling']
              },
              {
                icon: <Package className="w-12 h-12" />,
                title: 'Parcel & Package Services',
                description: 'Door-to-door delivery with volume discounts and insurance coverage included.',
                features: ['Door-to-door delivery', 'Volume discounts', 'Insurance included']
              },
              {
                icon: <Truck className="w-12 h-12" />,
                title: 'Haulage & Heavy Cargo',
                description: 'Nationwide truck fleet handling any size and weight with professional service.',
                features: ['Nationwide fleet', 'Any size/weight', 'Professional handling']
              },
              {
                icon: <Plane className="w-12 h-12" />,
                title: 'International Shipping',
                description: 'Global network via Skynet with customs clearance and air/sea freight options.',
                features: ['Global network', 'Customs clearance', 'Air & sea freight']
              }
            ].map((service, index) => (
              <div key={index} className="bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 group hover:scale-105">
                <div className="w-18 h-18 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-red-600 transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-500">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-red-600 to-red-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-8">
            Ready to Ship with Nigeria's Most Trusted Logistics Partner?
          </h2>
          <p className="text-xl text-red-100 mb-10 max-w-3xl mx-auto">
            Join thousands of satisfied customers who rely on CFL Global for their shipping needs across Nigeria and worldwide.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a href='/quote' className="bg-white text-red-600 px-10 py-5 rounded-2xl font-bold text-lg hover:bg-gray-50 transition-all duration-300 shadow-2xl transform hover:scale-105">
              Start Shipping Today
            </a>
            <a href='/contact' className="border-3 border-white text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-white hover:text-red-600 transition-all duration-300 transform hover:scale-105">
              Speak with an Expert
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center space-x-2 mb-6">
                <a href='/' className="flex items-center space-x-2">
                  <img
                    src="logo.png"
                    alt="CFL Global Logistics"
                    className="w-50 h-12 rounded-xl object-contain"
                  />
                  <span className="text-xl font-bold text-white">CFL Global</span>
                </a>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Your trusted logistics partner in Nigeria, connected to the world through the Skynet Worldwide Express network.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Express Document Delivery</li>
                <li>Parcel & Package Services</li>
                <li>Haulage & Heavy Cargo</li>
                <li>International Shipping</li>
                <li>Customs Clearing</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/tracking" className="hover:text-white transition-colors">Track Package</a></li>
                <li><a href="/quote" className="hover:text-white transition-colors">Get Quote</a></li>
                <li><a href="/services" className="hover:text-white transition-colors">Our Services</a></li>
                <li><a href="/about" className="hover:text-white transition-colors">About Us</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contact Info</h4>
              <div className="space-y-3 text-gray-400">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <div>
                    <div>08023693696</div>
                    <div>08037921992</div>
                  </div>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <div className="text-sm">
                    <div>Mon-Fri: 8:00 AM - 6:00 PM</div>
                    <div>Sat: 9:00 AM - 4:00 PM</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 CFL Global Logistics Limited. All rights reserved. | A member of Skynet Worldwide Express</p>
          </div>
        </div>
      </footer>

      {/* Custom Animations */}
      <style >{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes countUp {
          from {
            transform: scale(0.5);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-slideInUp {
          animation: slideInUp 1s ease-out;
        }

        .animate-countUp {
          animation: countUp 0.8s ease-out;
        }

        .animate-fadeIn {
          animation: fadeIn 1s ease-out;
        }
      `}</style>
    </div>
  );
};

export default HomePage;